import { Injectable, computed, effect, inject, signal } from '@angular/core';

import { AuthService } from '@app/core/services/auth.service';
import type {
  CorporateBusinessDetailsFormData,
  CorporateCompanyInfoFormData,
  CorporateSourceOfWealthFormData,
  OnboardingAccountType,
  PersonalProfileFormData,
  PersonalSourceOfFundsFormData,
  RegisterCorporateDto,
  RegisterPersonalDto,
} from '@app/core/models';

/**
 * Cross-step state container for the CW-13e onboarding wizard.
 *
 * Holds the user's selections from the account-type page through to the
 * final submit. In-memory only — no persistence. Three reset layers
 * enforce the no-resume policy (spec §4.10):
 *
 *   1. This service: auto-`reset()` whenever the authenticated user
 *      transitions (login or logout) so that a wizard left mid-flight
 *      can't survive an authenticated session.
 *   2. `AccountTypePageComponent.ngOnInit()` calls `reset()` on every
 *      mount — entering the wizard always starts fresh.
 *   3. `onboardingStepGuard` redirects deep links into stale state to
 *      `/onboarding/account-type`, which triggers layer 2.
 *
 * `getFinalPayload()` strips the frontend-only `confirmPassword` field
 * before returning the network DTO. It throws if state is incomplete —
 * callers should gate on `canSubmit()` first.
 *
 * Both personal and corporate branches of `getFinalPayload()` are
 * wired — the service is the single chokepoint for confirmPassword
 * stripping and optional-field pruning for both flows.
 */
@Injectable({ providedIn: 'root' })
export class OnboardingStateService {
  private readonly auth = inject(AuthService);

  // ── Writable signals (private) ──
  private readonly _accountType = signal<OnboardingAccountType | null>(null);
  private readonly _personalProfile = signal<PersonalProfileFormData | null>(null);
  private readonly _personalSourceOfFunds = signal<PersonalSourceOfFundsFormData | null>(null);
  private readonly _corporateCompanyInfo = signal<CorporateCompanyInfoFormData | null>(null);
  private readonly _corporateBusinessDetails = signal<CorporateBusinessDetailsFormData | null>(null);
  private readonly _corporateSourceOfWealth = signal<CorporateSourceOfWealthFormData | null>(null);

  // ── Read-only signals (public) ──
  readonly accountType = this._accountType.asReadonly();
  readonly personalProfile = this._personalProfile.asReadonly();
  readonly personalSourceOfFunds = this._personalSourceOfFunds.asReadonly();
  readonly corporateCompanyInfo = this._corporateCompanyInfo.asReadonly();
  readonly corporateBusinessDetails = this._corporateBusinessDetails.asReadonly();
  readonly corporateSourceOfWealth = this._corporateSourceOfWealth.asReadonly();

  // ── Computed convenience signals ──

  /** Convenience alias of `accountType` — clearer at call sites. */
  readonly currentFlow = computed(() => this._accountType());

  /** Number of completed wizard steps (used by the progress indicator). */
  readonly completedSteps = computed(() => {
    let count = 0;
    if (this._accountType() !== null) count++;
    if (this._accountType() === 'personal') {
      if (this._personalProfile() !== null) count++;
      if (this._personalSourceOfFunds() !== null) count++;
    } else if (this._accountType() === 'corporate') {
      if (this._corporateCompanyInfo() !== null) count++;
      if (this._corporateBusinessDetails() !== null) count++;
      if (this._corporateSourceOfWealth() !== null) count++;
    }
    return count;
  });

  /**
   * True when every step required for the current flow has been
   * populated and a `getFinalPayload()` call would succeed.
   */
  readonly canSubmit = computed(() => {
    if (this._accountType() === 'personal') {
      return this._personalProfile() !== null && this._personalSourceOfFunds() !== null;
    }
    if (this._accountType() === 'corporate') {
      return (
        this._corporateCompanyInfo() !== null &&
        this._corporateBusinessDetails() !== null &&
        this._corporateSourceOfWealth() !== null
      );
    }
    return false;
  });

  /** Whether the personal/profile step's form data has been saved. */
  readonly personalProfileComplete = computed(() => this._personalProfile() !== null);
  /** Whether the corporate/company-info step's form data has been saved. */
  readonly corporateCompanyInfoComplete = computed(() => this._corporateCompanyInfo() !== null);
  /** Whether the corporate/business-details step's form data has been saved. */
  readonly corporateBusinessDetailsComplete = computed(
    () => this._corporateBusinessDetails() !== null,
  );

  constructor() {
    // Auto-reset on any auth-state transition. Effects on signal-tracked
    // sources fire once on initialisation (with the current value) and
    // again on every change — that's harmless: the first emission resets
    // an already-empty state (no-op), and any subsequent login / logout
    // drops in-memory wizard data so a new session cannot inherit it.
    let prev: ReturnType<typeof this.auth.currentUser> | undefined;
    effect(() => {
      const next = this.auth.currentUser();
      if (prev !== undefined && next !== prev) {
        this.reset();
      }
      prev = next;
    });
  }

  // ── Mutators ──────────────────────────────────────────────────────

  /**
   * Pick the wizard branch. Clears state for the OPPOSITE branch so
   * the user switching choice at step 1 doesn't drag stale data forward.
   */
  setAccountType(type: OnboardingAccountType): void {
    this._accountType.set(type);
    if (type === 'personal') {
      this._corporateCompanyInfo.set(null);
      this._corporateBusinessDetails.set(null);
      this._corporateSourceOfWealth.set(null);
    } else {
      this._personalProfile.set(null);
      this._personalSourceOfFunds.set(null);
    }
  }

  setPersonalProfile(data: PersonalProfileFormData): void {
    this._personalProfile.set(data);
  }

  setPersonalSourceOfFunds(data: PersonalSourceOfFundsFormData): void {
    this._personalSourceOfFunds.set(data);
  }

  setCorporateCompanyInfo(data: CorporateCompanyInfoFormData): void {
    this._corporateCompanyInfo.set(data);
  }

  setCorporateBusinessDetails(data: CorporateBusinessDetailsFormData): void {
    this._corporateBusinessDetails.set(data);
  }

  setCorporateSourceOfWealth(data: CorporateSourceOfWealthFormData): void {
    this._corporateSourceOfWealth.set(data);
  }

  /** Drop every signal back to its initial null state. */
  reset(): void {
    this._accountType.set(null);
    this._personalProfile.set(null);
    this._personalSourceOfFunds.set(null);
    this._corporateCompanyInfo.set(null);
    this._corporateBusinessDetails.set(null);
    this._corporateSourceOfWealth.set(null);
  }

  // ── Final payload ────────────────────────────────────────────────

  /**
   * Assemble the register DTO from the per-step state. ALWAYS strips
   * `confirmPassword` — this method is the single chokepoint that
   * guarantees it never reaches the network.
   *
   * Throws if state is incomplete — `canSubmit()` should be checked
   * first.
   */
  getFinalPayload(): RegisterPersonalDto | RegisterCorporateDto {
    if (!this.canSubmit()) {
      throw new Error('Incomplete onboarding state');
    }
    const type = this._accountType();
    if (type === 'personal') {
      const profile = this._personalProfile();
      const sof = this._personalSourceOfFunds();
      if (!profile || !sof) {
        throw new Error('Personal registration state incomplete');
      }
      // Bug 2 (2026-05-26): `dialingCode` now carries the country list
      // ID (number) from DialingCodePicker. Send it as a digit-string
      // for the backend `countryCode` field so Common Auth resolves the
      // correct country. `phoneNumber` is digits-only — no `+countryCode`
      // prefix embedded (that was causing the double "+49 +491234" bug).
      if (profile.dialingCode === null) {
        throw new Error('Country code is required');
      }
      const payload: RegisterPersonalDto = {
        email: profile.email,
        password: profile.password,
        userName: profile.userName.trim(),
        phoneNumber: profile.phoneNumber,
        dateOfBirth: profile.dateOfBirth,
        streetLine1: profile.streetLine1,
        city: profile.city,
        postalCode: profile.postalCode,
        country: profile.country,
        countryCode: String(profile.dialingCode),
        primarySource: sof.primarySource,
        estimatedAnnualAmount: sof.estimatedAnnualAmount,
        // Multi-currency (2026-05-24) — election picked at the personal
        // profile step flows through to the wire so the backend can
        // provision 1 or 2 TrustAccounts.
        selectedCurrency: profile.selectedCurrency,
      };
      // Pruning of optional fields — backend uses `@IsOptional`, so
      // omitting empty strings keeps the payload minimal and matches
      // the spec contract.
      if (profile.streetLine2 && profile.streetLine2.length > 0) {
        payload.streetLine2 = profile.streetLine2;
      }
      if (profile.googlePlaceId && profile.googlePlaceId.length > 0) {
        payload.googlePlaceId = profile.googlePlaceId;
      }
      if (sof.additionalDetails && sof.additionalDetails.length > 0) {
        payload.additionalDetails = sof.additionalDetails;
      }
      return payload;
    }
    if (type === 'corporate') {
      const company = this._corporateCompanyInfo();
      const business = this._corporateBusinessDetails();
      const wealth = this._corporateSourceOfWealth();
      if (!company || !business || !wealth) {
        throw new Error('Corporate registration state incomplete');
      }
      // Bug 2 (2026-05-26): see personal branch above for the semantic
      // change — `dialingCode` is now the country list ID (number).
      if (company.dialingCode === null) {
        throw new Error('Country code is required');
      }
      const payload: RegisterCorporateDto = {
        // === REQUIRED AUTH CREDENTIALS (always present, confirmPassword stripped) ===
        email: company.email,
        password: company.password,
        // === OWNER IDENTITY ===
        userName: company.userName.trim(),
        phoneNumber: company.phoneNumber,
        dateOfBirth: company.dateOfBirth,
        // === ADDRESS ===
        streetLine1: company.streetLine1,
        city: company.city,
        postalCode: company.postalCode,
        country: company.country,
        countryCode: String(company.dialingCode),
        // === COMPANY ===
        companyName: company.companyName.trim(),
        registrationNumber: company.registrationNumber,
        registrationCountry: company.registrationCountry,
        industryType: company.industryType,
        yearEstablished: company.yearEstablished,
        // === BUSINESS DETAILS ===
        businessActivity: business.businessActivity,
        expectedMonthlyTransactionVolume: business.expectedMonthlyTransactionVolume,
        numberOfEmployees: business.numberOfEmployees,
        targetMarkets: business.targetMarkets,
        // === SOURCE OF WEALTH ===
        primarySource: wealth.primarySource,
        estimatedAnnualAmount: wealth.estimatedAnnualAmount,
        // === MULTI-CURRENCY ELECTION (2026-05-24) ===
        // The corporate picked this on the company-info step; backend
        // provisions 1 or 2 TrustAccounts under the owner's userId.
        selectedCurrency: company.selectedCurrency,
      };
      // Prune optional fields — backend uses `@IsOptional`, so omitting
      // empty strings keeps the payload minimal and matches the spec contract.
      if (company.streetLine2 && company.streetLine2.length > 0) {
        payload.streetLine2 = company.streetLine2;
      }
      if (company.googlePlaceId && company.googlePlaceId.length > 0) {
        payload.googlePlaceId = company.googlePlaceId;
      }
      if (company.websiteUrl && company.websiteUrl.length > 0) {
        payload.websiteUrl = company.websiteUrl;
      }
      if (wealth.additionalDetails && wealth.additionalDetails.length > 0) {
        payload.additionalDetails = wealth.additionalDetails;
      }
      return payload;
    }
    throw new Error('No account type selected');
  }
}
